import { unwrapResult } from '@reduxjs/toolkit';
import { useAppSelector } from 'hooks';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { MainMenuItemEditModel, MainMenuItemModel } from 'types/entity';
import { mainMenuUpdateQuery } from './main-menu-update.selector';
import { mainMenuUpdateActions } from './main-menu-update.slice';

export const useMainMenuUpdateFacade = () => {
  const dispatch: AppDispatch = useDispatch();

  /** ロード済みか */
  const loaded = useAppSelector(mainMenuUpdateQuery.getLoaded);
  /** 編集中か */
  const touched = useAppSelector(mainMenuUpdateQuery.getTouched);
  /** 一度でも適用したことがあるか */
  const applied = useAppSelector(mainMenuUpdateQuery.getApplied);
  /** 編集中のメインメニューアイテム一覧 */
  const editingItems: MainMenuItemEditModel[] = useAppSelector(mainMenuUpdateQuery.getEditingItems);
  /** 編集中のメインメニューID */
  const editingMainMenuId: string = useAppSelector(mainMenuUpdateQuery.getEditingMainMenuId);
  /** メインメニューマップ */
  const mainMenuMap: { [main_menu_id: string]: MainMenuItemEditModel[]} = useAppSelector(mainMenuUpdateQuery.getFixedEntities);
  /** メインメニューマップ（LOCALE指定） */
  const mainMenuMapByLocale: { [main_menu_id: string]: MainMenuItemModel[]} = useAppSelector(mainMenuUpdateQuery.getFixedEntitiesByLocale);

  /** ロード */
  const loadMainMenu = useCallback(
    (projectId: string, mainMenuId: string) => dispatch(mainMenuUpdateActions.loadAdminMainMenu({ projectId, mainMenuId })).then(unwrapResult),
    [dispatch],
  );
  /** 編集開始 */
  const startEdit = useCallback(
    (mainMenuId: string) => dispatch(mainMenuUpdateActions.startEdit(mainMenuId)),
    [dispatch],
  );
  /** 編集キャンセル */
  const cancelEdit = useCallback(
    () => dispatch(mainMenuUpdateActions.cancelEdit()),
    [dispatch],
  );
  /** 編集状態を解除 */
  const clearAll = useCallback(
    () => dispatch(mainMenuUpdateActions.clearAll()),
    [dispatch],
  );
  /** 編集中データを適用 */
  const apply = useCallback(
    () => dispatch(mainMenuUpdateActions.apply()),
    [dispatch],
  );
  /** 編集中状態を初期化 */
  const unApplied = useCallback(
    () => dispatch(mainMenuUpdateActions.unApplied()),
    [dispatch],
  );
  /** メインメニューアイテムを追加 */
  const upsertItem = useCallback(
    (item: MainMenuItemEditModel) => dispatch(mainMenuUpdateActions.upsertItem(item)),
    [dispatch],
  );
  /** 指定したメインメニューアイテム一覧で更新する */
  const replaceItems = useCallback(
    (items: MainMenuItemEditModel[]) => dispatch(mainMenuUpdateActions.replaceItems(items)),
    [dispatch],
  );
  // メインメニューガジェット追加時に空のメインメニューを作成する
  const addEmptyMainMenu = useCallback(
    (mainMenuId: Required<MainMenuItemModel>['main_menu_id']) => dispatch(mainMenuUpdateActions.addEmptyMainMenu({ mainMenuId })),
    [dispatch],
  );

  return {
    loaded,
    touched,
    applied,
    editingItems,
    editingMainMenuId,
    mainMenuMap,
    mainMenuMapByLocale,
    loadMainMenu,
    startEdit,
    cancelEdit,
    clearAll,
    apply,
    unApplied,
    upsertItem,
    replaceItems,
    addEmptyMainMenu,
  };
};
